import { z } from 'zod';

export const resourceTypeEnum = z.enum(['video', 'post', 'tip']);
export type ResourceType = z.infer<typeof resourceTypeEnum>;

export const resourceStatusEnum = z.enum(['draft', 'published', 'archived']);
export type ResourceStatus = z.infer<typeof resourceStatusEnum>;

export const resourcePoolEnum = z.enum(['institution', 'corporate', 'b2c']);
export type ResourcePool = z.infer<typeof resourcePoolEnum>;

/**
 * Admin-facing engagement resource shape.
 *
 * Mirrors `EngagementResource` in
 * `docs/client-frontend/9_engagement-library.md` (§"TypeScript Types").
 *
 * Fields are tolerant by design — different endpoints surface different
 * subsets:
 *   - `GET /engagement/resources` (list) returns raw `s3ContentKey`/
 *     `s3ThumbnailKey`; client-facing endpoints strip them.
 *   - `GET /engagement/resources/:id` (detail) additionally populates
 *     `locationIds` and the presigned `contentDownloadUrl` /
 *     `thumbnailDownloadUrl` (~1h expiry).
 */
export const resourceSchema = z.object({
  id: z.string(),
  businessAssociateId: z.string(),
  createdById: z.string(),
  type: resourceTypeEnum,
  title: z.string(),
  description: z.string().nullish(),
  excerpt: z.string().nullish(),
  author: z.string().nullish(),
  category: z.string().nullish(),
  tags: z.array(z.string()).nullish(),
  ageRangeMin: z.number().int().nullish(),
  ageRangeMax: z.number().int().nullish(),
  pools: z.array(resourcePoolEnum).min(1),
  status: resourceStatusEnum,
  publishedAt: z.string().nullish(),
  readTime: z.string().nullish(),
  duration: z.string().nullish(),
  contentUrl: z.string().nullish(),
  // Raw S3 keys — only present on admin list/detail responses; client-facing
  // endpoints strip them and replace with the presigned download URLs below.
  s3ContentKey: z.string().nullish(),
  s3ThumbnailKey: z.string().nullish(),
  // Presigned URLs — only present on detail (admin or self-help) responses.
  contentDownloadUrl: z.string().nullish(),
  thumbnailDownloadUrl: z.string().nullish(),
  // Pinned location restriction — only populated on detail. `[]` ⇒ BA-wide.
  locationIds: z.array(z.string()).nullish(),
  metadata: z.record(z.string(), z.unknown()).nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullish(),
});
export type Resource = z.infer<typeof resourceSchema>;

export const clientEngagementSummarySchema = z.object({
  last30Days: z.record(z.string(), z.number()),
  totalBookmarks: z.number(),
  totalLikes: z.number(),
});
export type ClientEngagementSummary = z.infer<typeof clientEngagementSummarySchema>;

/**
 * Mirrors `createEngagementResourceSchema` in
 * `elixaa-core/elixaa-api/src/schemas/engagementResourceSchema.ts`.
 *
 * Notes that BE enforces and we replicate so the form blocks before
 * shipping a doomed request:
 *   - `pools` must contain at least one entry.
 *   - `ageRangeMin` ≤ `ageRangeMax` when both are set.
 *   - `contentUrl` must be a valid URL (empty string is rejected — callers
 *     should omit the field rather than send `""`).
 */
export const createResourceInputSchema = z
  .object({
    title: z.string().min(1).max(255),
    type: resourceTypeEnum,
    description: z.string().max(2000).optional(),
    contentUrl: z.url().max(1000).optional(),
    excerpt: z.string().max(500).optional(),
    author: z.string().max(255).optional(),
    category: z.string().max(100).optional(),
    tags: z.array(z.string().max(50)).max(20).optional(),
    ageRangeMin: z.number().int().min(0).max(120).optional(),
    ageRangeMax: z.number().int().min(0).max(120).optional(),
    pools: z.array(resourcePoolEnum).min(1),
    readTime: z.string().max(50).optional(),
    duration: z.string().max(50).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    locationIds: z.array(z.string().max(50)).optional(),
  })
  .refine((d) => d.ageRangeMin == null || d.ageRangeMax == null || d.ageRangeMin <= d.ageRangeMax, {
    message: 'ageRangeMin must be <= ageRangeMax',
    path: ['ageRangeMax'],
  });
export type CreateResourceInput = z.infer<typeof createResourceInputSchema>;

export const updateResourceInputSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    type: resourceTypeEnum.optional(),
    description: z.string().max(2000).optional(),
    contentUrl: z.url().max(1000).optional(),
    excerpt: z.string().max(500).optional(),
    author: z.string().max(255).optional(),
    category: z.string().max(100).optional(),
    tags: z.array(z.string().max(50)).max(20).optional(),
    ageRangeMin: z.number().int().min(0).max(120).optional(),
    ageRangeMax: z.number().int().min(0).max(120).optional(),
    pools: z.array(resourcePoolEnum).min(1).optional(),
    readTime: z.string().max(50).optional(),
    duration: z.string().max(50).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    locationIds: z.array(z.string().max(50)).optional(),
    // s3 keys are server-managed (populated by the request-upload-url flow).
    // Clients may only send `null` to clear an existing key — used by the
    // "Remove file" affordance on the upload field so a video resource can
    // be switched cleanly from S3 upload back to an external URL.
    s3ContentKey: z.null().optional(),
    s3ThumbnailKey: z.null().optional(),
  })
  .refine((d) => d.ageRangeMin == null || d.ageRangeMax == null || d.ageRangeMin <= d.ageRangeMax, {
    message: 'ageRangeMin must be <= ageRangeMax',
    path: ['ageRangeMax'],
  });
export type UpdateResourceInput = z.infer<typeof updateResourceInputSchema>;

export const requestResourceUploadUrlSchema = z.object({
  resourceId: z.string().min(1).max(50),
  uploadType: z.enum(['content', 'thumbnail']),
  contentType: z.string().min(1).max(100),
  filename: z.string().min(1).max(255),
  // Card artwork COMPOSITION, only meaningful when `uploadType` is
  // `'thumbnail'`. Rides the presign so the BE writes it in the same
  // transaction as the key — otherwise the row would briefly point at new
  // artwork carrying the previous image's composition. Omitted when detection
  // could not decide, and the BE then clears the column rather than keeping a
  // stale value.
  isIllustration: z.boolean().optional(),
});
export type RequestResourceUploadUrlInput = z.infer<typeof requestResourceUploadUrlSchema>;

export const resourceUploadUrlResponseSchema = z.object({
  uploadUrl: z.url(),
  key: z.string(),
});
export type ResourceUploadUrlResponse = z.infer<typeof resourceUploadUrlResponseSchema>;

export const resourceLocationsBodySchema = z.object({
  locationIds: z.array(z.string().min(1).max(50)).min(1).max(100),
});
export type ResourceLocationsInput = z.infer<typeof resourceLocationsBodySchema>;

export interface ResourceListParams {
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'title' | 'publishedAt';
  sortOrder?: 'asc' | 'desc';
  search?: string;
  type?: ResourceType[];
  status?: ResourceStatus[];
  pool?: ResourcePool[];
  category?: string;
  locationId?: string;
}

export interface PaginatedResources {
  data: Resource[];
  pagination: { page: number; limit: number; total: number };
}
